import type { FunctionComponent } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '@dr.pogodin/react-utils';

import { useSession } from 'hooks';

import { createExperience, getExperience } from 'services/experience';
import { createSession } from 'services/session';

import {
  type ExperienceT,
  P_AIAPP_SESSION,
  type SessionKindT,
  type SessionT,
  useGlobalState,
} from 'utils';

import buttonTheme from 'themes/Button/ai-app/green-h52.scss';

import { usePlayback } from '../Playback';

type PropsT = {
  allowUnprepared?: boolean;
  checkInId: null | string;
  experience: ExperienceT;
  sessionKind: SessionKindT | null;
  setExperience: (e: ExperienceT) => void;
  title: string;
};

export const LaunchExperienceButton: FunctionComponent<PropsT> = ({
  allowUnprepared,
  checkInId,
  experience,
  sessionKind,
  setExperience,
  title,
}) => {
  const [userId] = useGlobalState('user.id');
  if (!userId) throw Error('Not authenticated');

  const navigate = useNavigate();
  const session = useSession();

  const {
    playTransition,
    prepare,
    prepareStatus,
    unlock,
  } = usePlayback();

  return (
    <Button
      // Gate on prefetch: segment 0 must be loaded before the unlock tap.
      disabled={
        !experience.isAvailable
        || (!allowUnprepared && prepareStatus !== 'ready')
      }
      onClick={() => {
        void (async () => {
          setExperience(experience);

          if (prepareStatus !== 'ready') await prepare(experience.videos);

          // Fade the full-screen logo in immediately on the tap, so it masks
          // the async session creation + navigation; the Session page fades it
          // back out once segment 0 is playing.
          playTransition();

          // Unlock the prepared shared <video> SYNCHRONOUSLY inside the tap
          // (the iOS audio gesture) before any async work; Session then adopts
          // the now-unlocked element and swaps src per segment.
          unlock();

          await session.reload(async () => {
            if (!userId) throw Error('Internal error');

            let experienceId = experience.id;

            // This means the page was hit directly, as the manual experience
            // build, rather than entered from the AI check-in flow (which
            // passes in an AI-created experience). It's also the case when
            // an existing experience was opened for editing and should be
            // saved as a new one, rather than overwriting the original.
            //
            // In this case we should save the experience in the backend.
            if (!experienceId) {
              // TODO: We should change createExperience() function
              // to return the saved experience (which might have some
              // additional data injected in addition to what we saved),
              // however, no time to look into it now.
              experienceId = await createExperience(experience);

              const e = await getExperience(experienceId);
              setExperience(e);
            }

            const newSession: SessionT = {
              checkInId,
              experienceId,
              kind: sessionKind,
              userId,
            };
            newSession.id = await createSession(newSession);
            return newSession;
          });
          await navigate(P_AIAPP_SESSION);
        })();
      }}
      theme={buttonTheme}
    >
      {title}
    </Button>
  );
};
